import * as React from 'react';
import PropTypes from 'prop-types';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import SimpleLink from 'components/common/links/SimpleLink.js';
import { STATIC_TABLE_SETTINGS } from 'store/reducers/constants/constants.js';

export default function MismatchWarningDialog(props) {
  const { onClose, value: valueProp, open } = props;
  const [values, setValues] = React.useState([]);
  const [step, setStep] = React.useState(0);
  const radioGroupRef = React.useRef(null);
  const staticTableSettings = STATIC_TABLE_SETTINGS
  const theme = useTheme();

  React.useEffect(() => {
    if (!open) {
        setValues(valueProp);
    }
  }, [valueProp, open]);


  const handleCancel = () => {
    onClose('cancel');
  };

  const handleOk = () => {
    onClose(values);
  };

  const handleChange = (event) => {
    const newValues = [...values];
    newValues.splice(step-1,4, event.target.value)
    setValues(newValues);
  };

  React.useEffect(() => {
    const newValues = (props.mismatches ?? []).map(() => null);
    setValues(newValues);
  }, [props.mismatches])


  let mismatchTable = null;
  let mismatchColumn = null;
  let mismatchNewValues = null;
  let mismatchSourceColumn = null;
  let rowsImpacted = 0;
  let valueLink = null;
  let tooltipHtml = null;
  let valueTooltip = null;
  if (props.mismatches && step > 0) {
    mismatchTable = props.mismatches[step-1]['tableName'];
    mismatchColumn = props.mismatches[step-1]['columnName'];
    mismatchNewValues = props.mismatches[step-1]['newValues'];
    mismatchSourceColumn = props.mismatches[step-1]['sourceColumn'];
    rowsImpacted = props.data.filter((obj) => {return mismatchNewValues.has(obj[mismatchSourceColumn])}).length;

    valueLink = <SimpleLink text="values" color={theme.palette.primary.main} hoverColor={theme.palette.primary.linkHover}/>;
    tooltipHtml = 
                <List
                    sx={{ width: '100%', maxWidth: 360}}
                    component="nav"
                    aria-labelledby="nested-list-subheader"
                    style={{
                    position: 'relative',
                    overflow: 'auto',
                    maxHeight: 400,
                    overflowY:'scroll',
                    padding:'0px',
                    border:'4px solid' + theme.palette.primary.main,
                    borderRadius: '5px',
                    '& ul': { padding: 0 },}}
                    >
                                <li>
                                    <ul style={{padding:'0px', margin:'0px', backgroundColor:'#fff'}}>
                                        <ListSubheader style={{lineHeight: '30px', fontSize: '16px', fontWeight: '700'}}>{`New ${mismatchSourceColumn}s`}</ListSubheader>
                                        {Array.from(mismatchNewValues).map((item) => (
                                        <ListItem key={String(item)} style={{paddingTop:'0px', paddingBottom:'0px'}}>
                                            <ListItemText style={{fontSize:'0.5rem'}} primary={`${item}`} />
                                        </ListItem>
                                        ))}
                                    </ul>
                                    </li>
                    </List>

    valueTooltip = <Tooltip  style={{padding:'0px'}} componentsProps={{ tooltip: { style:{backgroundColor:'inherit'} } }} placement={'top'}
    title={tooltipHtml}><span style={{padding:'0px'}}>{valueLink}</span></Tooltip>
    }

  let primaryActionButton = null;
  if (props.mismatches?.length === 0) {
    primaryActionButton = (
      <Button color="success" variant="contained" onClick={() => handleOk()} sx={{ borderRadius: '6px', fontWeight: 600, px: 4 }}>
        Close
      </Button>
    );
  } else if (step < props.mismatches.length) {
    primaryActionButton = (
      <Button color="primary" variant="contained" onClick={() => setStep((step) => step + 1)} sx={{ borderRadius: '6px', fontWeight: 600, px: 4 }}>
        Next
      </Button>
    );
  } else {
    primaryActionButton = (
      <Button color="success" variant="contained" onClick={() => handleOk()} sx={{ borderRadius: '6px', fontWeight: 600, px: 4 }}>
        Submit
      </Button>
    );
  }

  return (
    <>
      {props.mismatches && (
        <Dialog
          sx={{
            '& .MuiDialog-paper': {
              width: '80%',
              maxHeight: 500,
              maxWidth: 700,
              borderRadius: '12px',
              overflow: 'hidden',
            },
          }}
          keepMounted={false}
          open={Boolean(props.mismatches && open)}
          onClick={(event) => event.stopPropagation()}
        >
          <DialogTitle
            sx={{
              backgroundColor: props.mismatches.length > 0 || props.incompleteMessage ? 'warning.extraLight' : 'success.extraLight',
              color: props.mismatches.length > 0 || props.incompleteMessage ? 'warning.main' : 'success.dark',
              fontWeight: 700,
              py: 2,
              px: 3,
              borderBottom: '1px solid',
              borderColor: props.mismatches.length > 0 || props.incompleteMessage ? 'warning.light' : 'success.light',
            }}
          >
            <Grid container alignItems="center">
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                {props.mismatches.length > 0 ? (
                  <>
                    <WarningAmberIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
                    Table Upload Warning
                  </>
                ) : (
                  <>
                    <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
                    Table Upload Report
                  </>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ mt: { xs: 1, md: 0 } }}>
                  <Stack direction="column" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                    {props.fileName && (
                      <Box sx={{ opacity: 0.8 }}>
                        File: <span style={{ fontWeight: 600 }}>{props.fileName}</span>
                      </Box>
                    )}
                    {props.targetTable && (
                      <Box sx={{ opacity: 0.8 }}>
                        Table: <span style={{ fontWeight: 600 }}>{props.targetTable}</span>
                      </Box>
                    )}
                  </Stack>
                  <Stack direction="column" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                    {props.data && (
                      <Box sx={{ opacity: 0.8 }}>
                        Rows: <span style={{ fontWeight: 600 }}>{props.data.length}</span>
                      </Box>
                    )}
                    {props.mismatches && (
                      <Box sx={{ opacity: 0.8 }}>
                        Warnings: <span style={{ fontWeight: 600 }}>{props.mismatches.length}</span>
                      </Box>
                    )}
                    {/* Without this the header read "Warnings: 0" directly above
                        a warning: that counter is the dropdown-mismatch list and
                        has never known whether a row was usable. */}
                    {props.incompleteRows > 0 && (
                      <Box sx={{ opacity: 0.8 }}>
                        Not ready: <span style={{ fontWeight: 600 }}>{props.incompleteRows}</span>
                        {props.incompleteRows === 1 ? ' row' : ' rows'}
                      </Box>
                    )}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent dividers sx={{ minHeight: '200px', py: 3, px: 3 }}>
            {step === 0 && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* `mismatches` is the DROPDOWN mismatch list — unrecognised
                    vehicle types and the like. On its own it said "Success! No
                    data mismatches were found" over rows the optimize button was
                    about to reject seconds later, because nothing here had asked
                    whether the rows were usable. `incompleteMessage` is that
                    question (store/functions/missingRequired), asked the same way
                    the validator asks it. */}
                {props.mismatches.length === 0 && props.incompleteMessage ? (
                  <>
                    <WarningAmberIcon color="warning" sx={{ fontSize: '3rem' }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'warning.main', mb: 1 }}>
                        Uploaded, but not ready to optimize
                      </Typography>
                      <Typography variant="body1">{props.incompleteMessage}</Typography>
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        The rows are in the table — fill the missing values in there, or upload
                        again with those columns included.
                      </Typography>
                      {props.suppliedNotice?.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            Filled in for you:
                          </Typography>
                          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {props.suppliedNotice.map((sentence) => (
                              <Typography component="li" variant="body2" key={sentence} sx={{ color: 'text.secondary' }}>
                                {sentence}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </>
                ) : props.mismatches.length === 0 ? (
                  <>
                    <CheckCircleOutlineIcon color="success" sx={{ fontSize: '3rem' }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'success.dark', mb: 1 }}>
                        Success!
                      </Typography>
                      <Typography variant="body1">File uploaded successfully. No data mismatches were found.</Typography>
                      {props.suppliedNotice?.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            Filled in for you:
                          </Typography>
                          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {props.suppliedNotice.map((sentence) => (
                              <Typography component="li" variant="body2" key={sentence} sx={{ color: 'text.secondary' }}>
                                {sentence}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <WarningAmberIcon color="warning" sx={{ fontSize: '3rem' }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'warning.main', mb: 1 }}>
                        Action Required
                      </Typography>
                      <Typography variant="body1">
                        File uploaded with {props.mismatches.length} warnings. Please review the details in the following steps to ensure data integrity.
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
            {step > 0 && (
              <Box>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Uploaded data in the <strong style={{ color: theme.palette.primary.main }}>{mismatchSourceColumn}</strong> field contained new {valueTooltip}
                  &nbsp;for the <strong>{mismatchColumn}</strong> of the <strong>{staticTableSettings[mismatchTable]['title']}</strong> table. Would you like to add
                  these {valueTooltip} to {staticTableSettings[mismatchTable]['title']}?
                </Typography>

                <RadioGroup ref={radioGroupRef} aria-label="mismatch-warning" name="mismatch-warning" value={values[step - 1]} onChange={handleChange} sx={{ backgroundColor: 'gray.100', borderRadius: '8px', p: 1 }}>
                  <FormControlLabel
                    value={'add'}
                    key={'add'}
                    control={<Radio />}
                    label={<Typography variant="body2">Add {valueTooltip} to {staticTableSettings[mismatchTable]['title']}</Typography>}
                    sx={{
                      m: 0,
                      p: 1,
                      borderRadius: '6px',
                      '&:hover': { backgroundColor: '#fff' },
                    }}
                  />
                  <FormControlLabel
                    value={'remove'}
                    key={'remove'}
                    control={<Radio />}
                    label={
                      <Typography variant="body2">
                        Remove uploaded rows containing new {valueTooltip} and proceed with upload. This will affect <strong>{rowsImpacted}</strong> rows.
                      </Typography>
                    }
                    sx={{
                      m: 0,
                      p: 1,
                      borderRadius: '6px',
                      '&:hover': { backgroundColor: '#fff' },
                    }}
                  />
                </RadioGroup>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, backgroundColor: 'gray.50', justifyContent: 'space-between' }}>
            <Box>
              {props.mismatches.length > 0 && (
                <Button onClick={() => setStep((step) => step - 1)} disabled={step === 0} variant="outlined" sx={{ borderRadius: '6px', fontWeight: 600 }}>
                  Back
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleCancel} color="error" variant="text" sx={{ borderRadius: '6px', fontWeight: 600 }}>
                Cancel upload
              </Button>

              {primaryActionButton}
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

MismatchWarningDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  value: PropTypes.array,
  mismatches: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  fileName: PropTypes.string,
  targetTable: PropTypes.string,
  /** Why these rows are not yet usable, or '' when they are. */
  incompleteMessage: PropTypes.string,
  /** How many rows that covers. */
  incompleteRows: PropTypes.number,
  /** One sentence per value the product supplied because the upload did not. */
  suppliedNotice: PropTypes.arrayOf(PropTypes.string),
};
